import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, pw);
      toast.success(`${t("portal.welcome")}, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-gradient-to-br from-pink-50 via-white to-rose-50" data-testid="login-page">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm w-full max-w-md p-8">
        <div className="w-12 h-12 cupe-gradient rounded-lg flex items-center justify-center mb-5"><LogIn className="text-white" /></div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{t("auth.loginTitle")}</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">{t("auth.email")}</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px] focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none" data-testid="login-email" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">{t("auth.password")}</span>
            <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px] focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none" data-testid="login-password" />
          </label>
          <button type="submit" disabled={busy} className="cupe-gradient text-white w-full py-3 rounded-md font-semibold min-h-[48px] disabled:opacity-60" data-testid="login-submit">
            {busy ? "…" : t("auth.loginBtn")}
          </button>
        </form>
        <div className="mt-5 text-sm text-slate-500 text-center">
          {t("auth.noAccount")} <Link to="/register" className="text-[#d81b7a] font-semibold hover:underline">{t("auth.registerBtn")}</Link>
        </div>
      </div>
    </div>
  );
}

export function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success(t("auth.registrationSuccess"));
      nav("/login");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-gradient-to-br from-pink-50 via-white to-rose-50" data-testid="register-page">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm w-full max-w-md p-8">
        <div className="w-12 h-12 cupe-gradient rounded-lg flex items-center justify-center mb-5"><UserPlus className="text-white" /></div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{t("auth.registerTitle")}</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">{t("auth.name")}</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px]" data-testid="register-name" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">{t("auth.email")}</span>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px]" data-testid="register-email" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">{t("auth.password")}</span>
            <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px]" data-testid="register-password" />
          </label>
          <button type="submit" disabled={busy} className="cupe-gradient text-white w-full py-3 rounded-md font-semibold min-h-[48px] disabled:opacity-60" data-testid="register-submit">
            {busy ? "…" : t("auth.registerBtn")}
          </button>
        </form>
        <div className="mt-5 text-sm text-slate-500 text-center">
          {t("auth.haveAccount")} <Link to="/login" className="text-[#d81b7a] font-semibold hover:underline">{t("auth.loginBtn")}</Link>
        </div>
      </div>
    </div>
  );
}
